import { CaretLeftIcon, CaretRightIcon, StarIcon } from '@phosphor-icons/react'
import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import Button from '@/src/components/core/Button'
import { formatGerman } from '@/src/components/core/lib/date'
import ShowMore from '@/src/components/core/ShowMore'
import Container from '@/src/components/layout/Container'

function avg(values: (number | null)[]): number {
  const valid = values.filter((v): v is number => v !== null)
  return valid.length > 0 ? valid.reduce((s, v) => s + v, 0) / valid.length : 5
}

function reviewAvg(
  ratings: FahrlehrerCh.TeacherProfileReviewRatings
): number | null {
  return avg([
    ratings.progress,
    ratings.explanations,
    ratings.learningGoals,
    ratings.attention,
    ratings.patience,
    ratings.scheduling,
    ratings.cleanliness,
    ratings.punctuality,
  ])
}

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

function categoryAvg(
  reviews: FahrlehrerCh.TeacherProfileReview[],
  key: keyof FahrlehrerCh.TeacherProfileReviewRatings
): number | null {
  return avg(reviews.map((r) => r.ratings[key]))
}

const CATEGORIES: {
  key: keyof FahrlehrerCh.TeacherProfileReviewRatings
  label: string
}[] = [
  { key: 'explanations', label: 'Erklärungen' },
  { key: 'progress', label: 'Fortschritt' },
  { key: 'learningGoals', label: 'Lernziele' },
  { key: 'attention', label: 'Aufmerksamkeit' },
  { key: 'patience', label: 'Geduld' },
  { key: 'scheduling', label: 'Flexibilität' },
  { key: 'cleanliness', label: 'Sauberkeit' },
  { key: 'punctuality', label: 'Pünktlichkeit' },
]

const Stars = ({ count }: { count: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <StarIcon
        key={i}
        weight={i < count ? 'fill' : 'regular'}
        size={20}
        className={i < count ? 'text-yellow-600' : 'text-gray-300'}
      />
    ))}
  </div>
)

const ReviewCard = ({
  review,
}: {
  review: FahrlehrerCh.TeacherProfileReview
}) => {
  const stars = Math.round(reviewAvg(review.ratings) ?? 5)

  return (
    <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white px-6 py-6 shadow-xs">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="font-bold">{review.firstName ?? 'anonym'}</p>
          <p className="text-sm text-gray-600">
            {formatGerman(review.date, 'dd. MMMM yyyy')}
          </p>
        </div>
        <Stars count={stars} />
      </div>
      {review.text && (
        <ShowMore>
          {({ clampRef, isClamped, isExpanded, setIsExpanded }) => (
            <div className="mt-4">
              <p
                ref={clampRef}
                className={clsx('text-gray-800', {
                  'line-clamp-4': !isExpanded,
                })}
              >
                {review.text}
              </p>
              {isClamped && !isExpanded && (
                <button
                  className="mt-1 text-sm font-bold text-blue-700"
                  onClick={() => setIsExpanded(true)}
                >
                  Mehr anzeigen
                </button>
              )}
            </div>
          )}
        </ShowMore>
      )}
    </div>
  )
}

const Reviews = ({ reviews }: FahrlehrerCh.TeacherProfile) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    breakpoints: {
      '(min-width: 768px)': { skipSnaps: true },
    },
  })
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on('select', onSelect).on('reInit', onSelect)
  }, [emblaApi, onSelect])

  if (reviews.length === 0) return null

  const allValues = reviews.flatMap((r) => [
    r.ratings.progress,
    r.ratings.explanations,
    r.ratings.learningGoals,
    r.ratings.attention,
    r.ratings.patience,
    r.ratings.scheduling,
    r.ratings.cleanliness,
    r.ratings.punctuality,
  ])
  const overall = avg(allValues)
  const visibleCategories = CATEGORIES.flatMap(({ key, label }) => {
    const score = categoryAvg(reviews, key)
    return score !== null ? [{ key, label, score }] : []
  })
    .sort((a, b) => b.score - a.score)
    .slice(0, 4)

  reviews.sort((a, b) => {
    if (!!a.text !== !!b.text) {
      return (b.text ? 1 : 0) - (a.text ? 1 : 0)
    }
    return new Date(b.date).getTime() - new Date(a.date).getTime()
  })

  return (
    <div className="mt-24 bg-linear-to-b from-gray-50 to-gray-100 py-16">
      <section id="reviews">
        <Container>
          <h2 className="text-4xl font-black md:text-10xl">Bewertungen</h2>
          <div className="mt-6 flex divide-x divide-gray-150">
            <div className="flex shrink-0 flex-col items-center pr-6">
              <p className="bg-linear-to-b from-blue-700 to-blue-800 bg-clip-text text-10xl leading-none font-black text-transparent">
                {fmt(overall)}
              </p>
              <p className="-mt-1 text-sm font-bold text-gray-800">von 5</p>
              <p className="mt-2 text-sm text-gray-800">
                {reviews.length} Bewertung{reviews.length !== 1 && 'en'}
              </p>
            </div>
            <div className="ml-6 flex flex-1 flex-col justify-center gap-1 md:max-w-40">
              {visibleCategories.map(({ key, label, score }) => (
                <div
                  key={key}
                  className="flex items-center justify-between gap-4"
                >
                  <span className="text-sm text-gray-800 md:text-base">
                    {label}
                  </span>
                  <span className="text-sm font-bold text-gray-800 md:text-base">
                    {fmt(score)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Container>
        <div ref={emblaRef} className="overflow-hidden pt-6 pb-10">
          <div className="flex items-start select-none">
            {reviews.map((review, i) => (
              <div
                key={i}
                className="ml-5 min-w-0 flex-[0_0_calc(100%-40px)] last:mr-5 min-[1342px]:first:ml-[calc((100%-1280px)/2)] min-[1342px]:last:mr-[calc((100%-1280px)/2)]! md:ml-6 md:flex-[0_0_400px] md:last:mr-6"
              >
                <ReviewCard review={review} />
              </div>
            ))}
          </div>
        </div>
        <Container>
          <div className="flex justify-end gap-3">
            <Button
              icon={<CaretLeftIcon weight="bold" />}
              size="icon-small"
              version="gray-outlined"
              onClick={() => emblaApi?.scrollPrev()}
              disabled={!canScrollPrev}
              aria-label="Vorherige Bewertung"
            />
            <Button
              icon={<CaretRightIcon weight="bold" />}
              size="icon-small"
              version="gray-outlined"
              onClick={() => emblaApi?.scrollNext()}
              disabled={!canScrollNext}
              aria-label="Nächste Bewertung"
            />
          </div>
        </Container>
      </section>
    </div>
  )
}

export default Reviews
